import { z } from 'zod/v4'
import {
  WorkflowNodeBaseSchema,
  TimeWindowSchema,
} from '../../../resources/types'

/**
 * Schedule configuration for delayed message sending.
 * When provided, creates a ScheduledMessage instead of sending immediately.
 */
const MessageScheduleSchema = z
  .object({
    mode: z.enum(['relative', 'absolute']).describe('Scheduling mode'),
    // Relative mode fields
    amount: z
      .union([z.number(), z.string()])
      .optional()
      .describe(
        'Amount of time units to wait (for relative mode). Strings are allowed so workflow YAML can pass Liquid such as {{ inputs.delay-minutes }}.',
      ),
    unit: z
      .enum([
        'second',
        'seconds',
        'minute',
        'minutes',
        'hour',
        'hours',
        'day',
        'days',
      ])
      .optional()
      .describe('Time unit (for relative mode)'),
    // Absolute mode fields
    'schedule-at': z
      .string()
      .optional()
      .describe('ISO timestamp to send at (for absolute mode)'),
    // Time windows (optional, for relative mode)
    windows: z
      .array(TimeWindowSchema)
      .optional()
      .describe('Time windows to constrain scheduling within'),
    // Base time for relative mode (optional) — previous message scheduledAt for cadence chaining
    from: z
      .string()
      .optional()
      .describe(
        'ISO datetime base for relative delay (e.g. previous message scheduledAt). When omitted, delay is from now.',
      ),
    // Cancel on activity
    'cancel-on-activity': z
      .boolean()
      .optional()
      .default(true)
      .describe(
        'Cancel scheduled message if recipient replies before send time',
      ),
  })
  .describe('Schedule configuration for delayed message sending')

const ThreadMessageSendInputsSchema = z.object({
  'thread-id': z.string().describe('Thread to send message in'),
  content: z.string().describe('Message content'),
  'communication-channel-id': z
    .string()
    .optional()
    .describe(
      'Channel to send through (optional - uses thread bound channel if not provided)',
    ),
  'content-type': z
    .enum(['TEXT', 'HTML', 'MARKDOWN'])
    .optional()
    .describe('Optional, defaults to TEXT'),
  sender: z
    .object({
      'member-id': z
        .string()
        .optional()
        .describe('Member ID for MEMBER participant'),
      'agent-id': z
        .string()
        .optional()
        .describe('Agent ID for AGENT participant'),
      'agent-handle': z
        .string()
        .optional()
        .describe('Agent handle for AGENT participant'),
      'workflow-id': z
        .string()
        .optional()
        .describe('Workflow definition ID for WORKFLOW participant'),
      'workflow-run-id': z
        .string()
        .optional()
        .describe('Workflow execution ID for WORKFLOW participant'),
      'batch-job-id': z
        .string()
        .optional()
        .describe('Batch job ID for BATCH_JOB participant'),
      kind: z
        .literal('SYSTEM')
        .optional()
        .describe('For generic system messages'),
    })
    .optional()
    .describe(
      'Sender participant - if not provided, auto-detects workflow context',
    ),
  schedule: MessageScheduleSchema.optional().describe(
    'Optional schedule configuration. When provided, creates a ScheduledMessage instead of sending immediately.',
  ),
})

const ThreadMessageSendOptionsSchema = z.object({})

export const ThreadMessageSendCommandSchema = WorkflowNodeBaseSchema.extend({
  service: z.object({
    name: z.literal('skedyul/threads'),
    cmd: z.literal('message.send'),
    options: ThreadMessageSendOptionsSchema.optional(),
  }),
  inputs: ThreadMessageSendInputsSchema,
})

export type ThreadMessageSendCommand = z.infer<
  typeof ThreadMessageSendCommandSchema
>

export type MessageScheduleInput = z.infer<typeof MessageScheduleSchema>
